import { ResultOf } from "@graphql-typed-document-node/core";
import { format } from "date-fns";
import { uniqBy } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "urql";

import { FullPageSpinner } from "@/components/Spinner";
import { StreamingChannelCreateDialog } from "@/components/dialogs/StreamingChannelCreateDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { getProviderDisplayName, getProviderIcon } from "@/utils/streaming-channel";

const useStreamingChannels_StreamingChannel = graphql(`
    fragment useStreamingChannels_StreamingChannel on StreamingChannel {
        id
        ...StreamingChannelTable_StreamingChannel
    }
`);

const useStreamingChannels = () => {
    const [streamingChannels, setStreamingChannels] = useState<
        ResultOf<typeof useStreamingChannels_StreamingChannel>[]
    >([]);

    const [result, refetch] = useQuery({
        query: graphql(`
            query streamingChannelsQuery {
                streamingChannels {
                    id
                    ...useStreamingChannels_StreamingChannel
                }
            }
        `),
    });

    useEffect(() => {
        if (result.data?.streamingChannels) {
            const channels = getFragmentData(
                useStreamingChannels_StreamingChannel,
                result.data.streamingChannels
            );
            setStreamingChannels(uniqBy(channels ?? [], "id"));
        }
    }, [result.data?.streamingChannels]);

    return {
        streamingChannels,
        loading: result.fetching,
        error: result.error,
        refetch,
    };
};

const StreamingChannelsRoute = () => {
    const { streamingChannels, loading, error, refetch } = useStreamingChannels();

    if (loading) {
        return <FullPageSpinner />;
    }

    if (error) {
        return <div className="mb-4 w-full space-y-4 py-4">Error {error.message}</div>;
    }

    return (
        <Tabs defaultValue="all" className="m-4">
            <TabsContent value="all">
                <StreamingChannelsTable
                    streamingChannelFragments={streamingChannels}
                    loading={loading}
                    onRefetch={refetch}
                />
            </TabsContent>
        </Tabs>
    );
};

export default StreamingChannelsRoute;

const StreamingChannelTable_StreamingChannel = graphql(`
    fragment StreamingChannelTable_StreamingChannel on StreamingChannel {
        id
        externalChannelHandle
        externalChannelId
        provider
        createdAt
        updatedAt
    }
`);

export function StreamingChannelsTable({
    streamingChannelFragments,
    loading,
    onRefetch,
}: {
    streamingChannelFragments: FragmentType<typeof StreamingChannelTable_StreamingChannel>[];
    loading: boolean;
    onRefetch: () => void;
}) {
    const streamingChannels = getFragmentData(
        StreamingChannelTable_StreamingChannel,
        streamingChannelFragments
    );
    const navigate = useNavigate();

    const streamingChannelsListRef = useRef<HTMLTableSectionElement>(null);

    return (
        <Card className="bg-brand-white text-brand-black rounded border-none">
            <CardHeader>
                <CardTitle>Streaming Channels</CardTitle>
            </CardHeader>
            <CardContent>
                <StreamingChannelCreateDialog onSuccess={onRefetch} />
                <Table>
                    <TableHeader>
                        <TableRow className="hover:bg-brand-gray-light">
                            <TableHead className="text-brand-gray-dark">ID</TableHead>
                            <TableHead className="text-brand-gray-dark">Provider</TableHead>
                            <TableHead className="text-brand-gray-dark">
                                External channel handle
                            </TableHead>
                            <TableHead className="text-brand-gray-dark">
                                External channel Id
                            </TableHead>
                            <TableHead className="text-brand-gray-dark">Created</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody ref={streamingChannelsListRef}>
                        {streamingChannels.map(channel => {
                            const Icon = getProviderIcon(channel.provider);
                            return (
                                <TableRow
                                    className="hover:bg-brand-purple cursor-pointer hover:bg-opacity-20"
                                    key={channel.id}
                                    onClick={() => {
                                        const selection = window.getSelection();
                                        if (selection && selection.toString().length > 0) {
                                            return;
                                        }
                                        navigate(`/streaming-channels/${channel.id}/edit`);
                                    }}
                                >
                                    <TableCell className="font-medium">
                                        <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">
                                            {channel.id}
                                        </code>
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        <div className="flex items-center gap-2">
                                            <span>
                                                <Icon />
                                            </span>
                                            <span>{getProviderDisplayName(channel.provider)}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {channel.externalChannelHandle}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        <code className="rounded bg-gray-100 px-1 py-0.5 text-sm">
                                            {channel.externalChannelId}
                                        </code>
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {format(new Date(channel.createdAt), "MMM dd, yyyy")}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {streamingChannels.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={5} className="py-8 text-center text-gray-500">
                                    No streaming channels found. Create one to get started.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
